// AI Plan Reader scan dialog — pick sheets → read → hand findings to the
// AI Assistant side panel. (Review now lives in TakeoffAIPanel.)
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function TakeoffAIReader({ open, onClose, takeoffId, drawing, currentPage, onFindings }) {
  const pageCount = drawing?.page_count || 1;
  const [pages, setPages] = useState([]);
  const [hint, setHint] = useState("");
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState("");

  useEffect(() => {
    if (open) { setPages([currentPage || 1]); setReading(false); setProgress(""); setHint(""); }
  }, [open, currentPage]);

  const togglePage = (p) => setPages((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p].sort((a, b) => a - b));

  const run = async () => {
    if (!pages.length) { toast.error("Pick at least one sheet"); return; }
    setReading(true);
    const findings = [];
    const issues = [];
    const stages = ["Reading drawing", "Identifying structures & pipes", "Reading schedules & legend", "Cross-checking labels"];
    for (let i = 0; i < pages.length; i++) {
      const p = pages[i];
      setProgress(`Sheet ${p} (${i + 1}/${pages.length}) — ${stages[i % stages.length]}… up to a minute per sheet`);
      try {
        const { data } = await api.post(`/takeoffs/${takeoffId}/ai-read`,
          { drawing_id: drawing.id, page: p, hint: hint || undefined }, { timeout: 180000 });
        findings.push(...data.items);
        issues.push(...(data.issues || []));
      } catch (e) {
        toast.error(`Sheet ${p}: ${e.response?.data?.detail || "AI read failed"}`);
      }
    }
    setReading(false);
    if (!findings.length && !issues.length) { toast.error("The AI couldn't find anything measurable — try another sheet"); return; }
    onFindings({ findings, issues });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !reading && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="ai-reader-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FF5F15]" /> AI Plan Reader
          </DialogTitle>
        </DialogHeader>
        {reading ? (
          <div className="py-10 flex flex-col items-center gap-3 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF5F15]" />
            <p className="text-xs text-zinc-500 px-4" data-testid="ai-reader-progress">{progress}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-zinc-500">
              The AI reads <b>{drawing?.name}</b> — pipes, manholes, schedules, legends, depths, invert levels —
              then you review every finding in the assistant panel before anything lands in the takeoff.
            </p>
            {pageCount > 1 ? (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Which sheets? (fewer = faster)</p>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                    <button key={p} onClick={() => togglePage(p)}
                      className={`w-9 h-9 text-xs font-mono font-bold border ${pages.includes(p) ? "border-[#FF5F15] bg-[#FF5F15]/10 text-[#FF5F15]" : "border-zinc-300 dark:border-white/15 text-zinc-500 hover:border-[#FF5F15]"}`}
                      data-testid={`ai-reader-page-${p}`}>{p}</button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-[11px] text-zinc-500">Single sheet — the whole drawing will be read.</p>
            )}
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Focus (optional)</span>
              <Input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="e.g. stormwater only, ignore existing services"
                className="h-9 rounded-sm text-xs mt-1" data-testid="ai-reader-hint" />
            </label>
            <Button onClick={run} className="w-full h-10 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-xs font-bold uppercase tracking-wider" data-testid="ai-reader-run">
              <Sparkles className="w-4 h-4 mr-2" /> Read {pages.length > 1 ? `${pages.length} sheets` : "plan"} with AI
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
